import React, { useState, useEffect, useMemo } from 'react';
import { defineMessages } from 'react-intl-next';

import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

import type { SpaceOverviewSwitcherQuery$data } from '../../__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { Card } from '../components/Card.tsx';

import { LinkPickerButton } from './LinkPickerButton.tsx';
import { LinkItem as Item, type LinkData } from './LinkItem.tsx';

const i18n = defineMessages({
	topDocs: {
		id: 'space-overview.smart-space-overview.top-docs-card.title',
		defaultMessage: 'Top docs',
		description: 'Title of the top docs card',
	},
});

type SmartSpaceOverviewFromSwitcher = NonNullable<
	SpaceOverviewSwitcherQuery$data['confluence_smartSpaceOverview']
>;

type TopLinksCardProps = {
	topLinks: SmartSpaceOverviewFromSwitcher['topLinks'];
};

const maxCount = 4;

export const TopLinksCard = ({ topLinks }: TopLinksCardProps) => {
	const queryLinks = useMemo<LinkData[]>(() => {
		const rawLinks = topLinks?.links;
		if (!rawLinks) {
			return [];
		}
		const mapped = rawLinks.reduce<LinkData[]>((acc, link) => {
			if (link?.url && link?.title) {
				acc.push({
					id: link.url,
					url: link.url,
					title: link.title,
					isPinned: link.isPinned ?? false,
				});
			}
			return acc;
		}, []);
		return mapped.slice(0, maxCount);
	}, [topLinks]);

	const [links, setLinks] = useState<LinkData[]>(queryLinks);

	useEffect(() => {
		setLinks(queryLinks);
	}, [queryLinks]);
	const isAtCapacity = links.length >= maxCount;

	const handleAddLink = () => {};

	const handleLinkSubmit = (linkData: { url: string; title: string }) => {
		const newLink: LinkData = {
			id: `${linkData.url}-${linkData.title}`,
			url: linkData.url,
			title: linkData.title,
			subtitle: new URL(linkData.url).hostname,
			isPinned: true,
		};

		setLinks((prevLinks) => [...prevLinks, newLink]);
	};

	const handleRemoveLink = (linkId: string) => {
		setLinks((prevLinks) => prevLinks.filter((link) => link.id !== linkId));
	};

	const handlePinLink = (linkId: string) => {
		setLinks((prevLinks) =>
			prevLinks.map((link) => (link.id === linkId ? { ...link, isPinned: !link.isPinned } : link)),
		);
	};

	const initialState = window['__SSR_TOP_DOCS__'];
	const storeData = useStoreOptions(initialState, isPageSSRd());
	const storeOptions = isPageSSRd() ? storeData : {};

	return (
		<ConfluenceSmartCardProvider authFlow="disabled" {...storeOptions}>
			<Card
				title={i18n.topDocs}
				count={links.length}
				maxCount={maxCount}
				actionButton={
					<LinkPickerButton
						onAddClick={handleAddLink}
						onLinkSubmit={handleLinkSubmit}
						disabled={isAtCapacity}
						existingLinks={links}
					/>
				}
			>
				{links.map((link) => (
					<Item
						key={link.id}
						link={link}
						onRemove={handleRemoveLink}
						onPin={handlePinLink}
						isPinned={link.isPinned}
					/>
				))}
			</Card>
		</ConfluenceSmartCardProvider>
	);
};
